/**
 * Plitkalar to'rini qatorlar bo'yicha teng taqsimlash.
 *
 * `repeat(auto-fit, minmax(…))` konteynerga nechta ustun sig'sa shuncha ustun ochadi: 6 ta plitka
 * 5 + 1, 9 ta plitka 5 + 4 bo'lib tushadi va oxirgi qatorda bo'sh joy (yoki yolg'iz plitka)
 * qoladi. Bu hook ustunlar sonini qatorlar sonini saqlagan holda kamaytiradi (6 → 3 + 3), oxirgi
 * qator to'lmasa uning plitkalari qolgan kenglikni teng bo'lib to'ldiradi. Imkon bo'lsa, qatorlari
 * to'la bo'ladigan ustun soni tanlanadi (9 ta plitka → 3 × 3).
 *
 * Ishlatilishi: `ref` va `style` ni to'rga bering, har bir plitkaga `spanFor(index)` beradi.
 * CSS tomonida ustun shabloni `repeat(var(--bal-cols, auto-fit), minmax(var(--bal-min, 170px), 1fr))`.
 */
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

export function useBalancedGrid(
  count: number,
  { min = 170, gap = 12, minCols = 1 }: { min?: number; gap?: number; minCols?: number } = {},
) {
  const ref = useRef<HTMLElement | null>(null);
  const [cols, setCols] = useState(0);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || count === 0) return undefined;
    const measure = () => {
      const fit = Math.max(minCols, Math.floor((node.clientWidth + gap) / (min + gap)));
      if (count <= fit) {
        setCols(count);
        return;
      }
      /* Avval qatorlari to'la bo'ladigan ustun soni (9 → 3 × 3, 6 → 3 + 3); bo'lmasa qatorlar
         soni saqlanib, ustunlar tenglashtiriladi va oxirgi qator to'ldiriladi. */
      let exact = 0;
      for (let candidate = fit; candidate >= Math.max(minCols, Math.ceil(fit / 2)); candidate -= 1) {
        if (count % candidate === 0) {
          exact = candidate;
          break;
        }
      }
      setCols(exact || Math.ceil(count / Math.ceil(count / fit)));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [count, min, gap, minCols]);

  const tail = cols > 0 ? count % cols : 0;

  /** Plitkaning necha ustun egallashi (oxirgi to'lmagan qatorda 1 dan katta bo'lishi mumkin). */
  const spanFor = useCallback(
    (index: number) => {
      const fromEnd = count - index;
      if (tail === 0 || fromEnd > tail) return 1;
      return Math.floor(cols / tail) + (tail - fromEnd < cols % tail ? 1 : 0);
    },
    [count, cols, tail],
  );

  const style: CSSProperties | undefined =
    cols > 0 ? ({ "--bal-cols": cols, "--bal-min": "0px" } as CSSProperties) : undefined;

  return { ref, style, spanFor };
}
